import React, { FC, useEffect, useRef } from 'react';
import styles from './styles.module.css';

export type RaffleConfirmationModalProps = {
  /** The unique raffle entry number assigned at submission (AC5). */
  raffleEntryNumber: number;
  /** Maximum prize amount for the raffle phase (e.g. 500). */
  maxPrize: number | null;
  /** Called when producer clicks "Return to Portal". */
  onClose: () => void;
  /** The element that triggered the modal — focus is returned here on close. */
  triggerRef?: React.RefObject<HTMLElement>;
};

export const RaffleConfirmationModal: FC<RaffleConfirmationModalProps> = ({
  raffleEntryNumber,
  maxPrize,
  onClose,
  triggerRef,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus the heading on mount so screen readers announce the dialog immediately.
  useEffect(() => {
    headingRef.current?.focus();
    // Snapshot the trigger at mount time — by unmount the ref may have changed.
    const triggerEl = triggerRef?.current ?? null;
    return () => {
      // Return focus to the trigger element when the modal unmounts.
      triggerEl?.focus();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Trap Tab/Shift-Tab focus within the modal while it is open.
  // Pressing Escape dismisses the dialog (a11y — keyboard dismiss).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  };

  return (
    <div
      className={styles.overlay}
      data-testid="raffle-confirmation-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="raffle-modal-heading"
      onKeyDown={handleKeyDown}
    >
      <div
        className={styles.modal}
        data-testid="raffle-confirmation-modal"
        ref={modalRef}
      >
        {/* Heading (AC5) */}
        <h2
          className={styles.heading}
          id="raffle-modal-heading"
          data-testid="raffle-modal-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          You&apos;re in the raffle
        </h2>

        {/* Max prize (AC5) */}
        {maxPrize != null && (
          <p className={styles.prizeText} data-testid="raffle-modal-prize">
            Win up to ${maxPrize}
          </p>
        )}

        {/* Body copy (AC5) */}
        <p className={styles.bodyText} data-testid="raffle-modal-body">
          Your entry is locked in. Winners will be announced at the close of Act
          2.
        </p>

        {/* Confirmation ID (AC5) */}
        <div
          className={styles.confirmationRow}
          data-testid="raffle-modal-confirmation-id-row"
        >
          <span className={styles.confirmationLabel}>Confirmation #</span>
          <span
            className={styles.confirmationId}
            data-testid="raffle-modal-confirmation-id"
          >
            {raffleEntryNumber}
          </span>
        </div>

        {/* Return to Portal button (AC5) */}
        <button
          className={styles.returnButton}
          onClick={onClose}
          data-testid="raffle-modal-return-button"
        >
          Return to Portal
        </button>
      </div>
    </div>
  );
};
